import { VehicleProfile, SubletExpense, LaborRecord } from '../types/dossier';
import { VisualizerState } from '../types/vehicle';
import { SubAssembly, HardwareBag, PhotoLog, RestorationTask } from '../types/teardown';
import { BOMItem } from '../types/bom';

export const DEFAULT_VEHICLE: VehicleProfile = {
  id: 'proj_charger_69_rt',
  title: '1969 Dodge Charger R/T 440 Magnum',
  year: 1969,
  make: 'Dodge',
  model: 'Charger',
  trim: 'R/T',
  vin: 'XS29L9B284192',
  cowlTag: 'XS29: Dodge Charger R/T Sports Hardtop | L9B: 440 375HP 4BBL V8 | Paint: T5 Bronze Metallic | Trim: C6X Black Vinyl',
  engineSpecs: 'Chrysler 440 ci RB Wedge Big Block (Forged Crank, Edelbrock Performer RPM Aluminum Heads, Comp Cams Thumpr Cam)',
  transmissionSpecs: 'TorqueFlite 727 Heavy-Duty 3-Speed Automatic with B&M Shift Kit',
  chassisSuspensionSpecs: 'QA1 Tubular K-Member, Tubular Upper/Lower Control Arms, Fox 2.0 Shocks, 1.25" Front Sway Bar, CalTracs Traction Bars',
  brakeSpecs: 'Wilwood Superlite 6-Piston Front / 4-Piston Rear Power Disc Brakes with 12.88" Slotted Rotors',
  paintCodeColor: 'T5 Bronze Metallic (Acrylic Lacquer) with Black Vinyl Top & Scat Pack Tailstripe',
  interiorColorTrim: 'Black Deluxe Vinyl Buckets, Factory Woodgrain Center Console, Restored Tic-Toc-Tach Cluster',
  odometerReading: 84210,
  acquisitionDate: '2024-04-15',
  acquisitionCost: 28500,
  budgetCap: 85000,
  createdAt: '2024-04-15T10:00:00.000Z',
  updatedAt: '2026-10-01T00:00:00.000Z',
};

export const DEFAULT_VISUALIZER: VisualizerState = {
  version: 1,
  id: 'vis_charger_69_rt',
  name: '1969 Charger Street Brawler Stance',
  updatedAt: '2026-10-01T00:00:00.000Z',
  silhouetteId: 'classic_muscle',
  stance: {
    frontHeightOffsetInches: -2.0,
    rearHeightOffsetInches: -0.75,
    frontWheelDiameter: 17,
    rearWheelDiameter: 18,
    staggeredWheels: true,
  },
  paint: {
    finishType: 'acrylic_lacquer',
    colorHex: '#78350f', // Burnt Bronze / T5 Bronze Metallic
    presetName: 'Burnt Bronze',
    clearcoatGloss: 0.94,
    metallicFleckDensity: 0.70,
  },
  wheel: {
    archetype: 'torq_thrusts',
    finish: 'machined_gray',
    tireTreatment: 'white_letter',
  },
  showDimensionHud: true,
};

export const DEFAULT_SUB_ASSEMBLIES: SubAssembly[] = [
  {
    id: 'sub_001',
    projectId: 'proj_charger_69_rt',
    phase: 'disassembly_and_tagging',
    binNumber: 'BIN-A01',
    name: 'Front Grille & Hideaway Headlight Doors',
    system: 'glass_trim',
    status: 'restoring',
    storageLocation: 'Mezzanine Rack 1, Shelf C',
    notes: 'Vacuum servos removed for rebuilding. Cast pivot brackets cleaned and bead blasted.',
    hardwareBagCount: 2,
    createdAt: '2024-05-01T09:00:00.000Z',
    updatedAt: '2024-06-12T14:30:00.000Z',
  },
  {
    id: 'sub_002',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    binNumber: 'BIN-B02',
    name: 'Front K-Member & Steering Linkage Assembly',
    system: 'chassis',
    status: 'ready_for_install',
    storageLocation: 'Shop Bay 2, Heavy Parts Dolly',
    notes: 'Powder-coated semi-gloss black. QA1 tubular upper and lower arms torqued with grease fittings primed.',
    hardwareBagCount: 3,
    createdAt: '2024-06-15T11:00:00.000Z',
    updatedAt: '2024-08-20T16:00:00.000Z',
  },
  {
    id: 'sub_003',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    binNumber: 'BIN-C03',
    name: '440 RB Short Block & Crankshaft Assembly',
    system: 'engine',
    status: 'ready_for_install',
    storageLocation: 'Engine Clean Room, Stand #2',
    notes: 'Forged 4.150 stroke steel crankshaft, Mahle pistons, ARP main studs torqued to 110 lb-ft.',
    hardwareBagCount: 2,
    createdAt: '2024-07-10T13:00:00.000Z',
    updatedAt: '2024-09-05T10:15:00.000Z',
  },
  {
    id: 'sub_004',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    binNumber: 'BIN-D04',
    name: 'Passenger Rear Quarter & Outer Wheelhouse',
    system: 'body',
    status: 'cleaning_prep',
    storageLocation: 'Body Shop Stall 4',
    notes: 'Old rust cut along factory body line. New AMD stamped steel replacement panel clecoed in place for spot welding.',
    hardwareBagCount: 1,
    createdAt: '2024-08-01T08:30:00.000Z',
    updatedAt: '2024-09-18T15:45:00.000Z',
  },
  {
    id: 'sub_005',
    projectId: 'proj_charger_69_rt',
    phase: 'electrical',
    binNumber: 'BIN-E05',
    name: 'Firewall Bulkhead & Engine Harness',
    system: 'electrical',
    status: 'cataloged',
    storageLocation: 'Electrical Bench, Drawer 3',
    notes: 'Original harness corroded at ammeter passthrough. Upgrading to American Autowire with blade fuse block.',
    hardwareBagCount: 1,
    createdAt: '2024-09-01T10:00:00.000Z',
    updatedAt: '2024-09-01T10:00:00.000Z',
  },
  {
    id: 'sub_006',
    projectId: 'proj_charger_69_rt',
    phase: 'interior',
    binNumber: 'BIN-F06',
    name: 'Rallye Instrument Cluster & Tic-Toc-Tach',
    system: 'interior',
    status: 'restoring',
    storageLocation: 'Interior Specialty Cabinet A',
    notes: 'Gauges recalibrated and quartz clock movement installed. Bezel woodgrain hydrodipped.',
    hardwareBagCount: 1,
    createdAt: '2024-09-10T14:00:00.000Z',
    updatedAt: '2024-09-25T11:00:00.000Z',
  },
  {
    id: 'sub_007',
    projectId: 'proj_charger_69_rt',
    phase: 'final_assembly',
    binNumber: 'BIN-G07',
    name: 'Front & Rear Triple Chrome Bumpers',
    system: 'glass_trim',
    status: 'cataloged',
    storageLocation: 'Padded Foam Rack, Level 3',
    notes: 'Received fresh show chrome from Tri-City Plating. Wrapped in microfoam until final body polish.',
    hardwareBagCount: 2,
    createdAt: '2024-09-15T09:30:00.000Z',
    updatedAt: '2024-09-15T09:30:00.000Z',
  }
];

export const DEFAULT_HARDWARE_BAGS: HardwareBag[] = [
  {
    id: 'bag_001',
    subAssemblyId: 'sub_001',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-001: Hideaway Grille Actuator Linkage & Bellcranks',
    binLocation: 'BIN-A01',
    status: 'plated_restored',
    notes: 'Re-plated in yellow cadmium. Retain original wave washers.',
    torqueSpec: '18 lb-ft with blue Loctite',
    reusable: true,
    replacementsOrdered: false,
    fasteners: [
      {
        id: 'fast_001',
        type: 'Hex Head Bolt',
        size: '5/16"-18 x 3/4"',
        grade: 'Grade 5',
        finish: 'Yellow Cadmium',
        quantityOriginal: 8,
        quantityUsable: 8,
        replacementRequired: false,
        headmarkNotes: 'Dodge DP stamped mark',
      },
      {
        id: 'fast_002',
        type: 'Wave Washer',
        size: '5/16" ID',
        grade: 'Stainless 304',
        finish: 'Raw / As-Found',
        quantityOriginal: 8,
        quantityUsable: 8,
        replacementRequired: false,
      }
    ],
    createdAt: '2024-05-02T10:00:00.000Z',
    updatedAt: '2024-06-10T12:00:00.000Z',
  },
  {
    id: 'bag_002',
    subAssemblyId: 'sub_002',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-002: Upper Control Arm Alignment Eccentric Cam Bolts',
    binLocation: 'BIN-B02',
    status: 'replaced',
    notes: 'Original bolts had stripped threads. Ordered Moog Grade 8 replacements.',
    torqueSpec: '75 lb-ft at curb ride height',
    reusable: false,
    replacementsOrdered: true,
    fasteners: [
      {
        id: 'fast_003',
        type: 'Hex Head Bolt',
        size: '1/2"-20 x 4-1/2" Eccentric Cam',
        grade: 'Grade 8',
        finish: 'Phosphate & Oil',
        quantityOriginal: 4,
        quantityUsable: 0,
        replacementRequired: true,
        headmarkNotes: 'Replaced with Moog K8243 cam bolts',
      },
      {
        id: 'fast_004',
        type: 'Stover Lock Nut',
        size: '1/2"-20 Prevailing Torque',
        grade: 'Grade 8',
        finish: 'Phosphate & Oil',
        quantityOriginal: 4,
        quantityUsable: 0,
        replacementRequired: true,
      }
    ],
    createdAt: '2024-06-16T14:00:00.000Z',
    updatedAt: '2024-07-02T09:30:00.000Z',
  },
  {
    id: 'bag_003',
    subAssemblyId: 'sub_003',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-003: 440 Cylinder Head Bolts & Washers',
    binLocation: 'BIN-C03',
    status: 'replaced',
    notes: 'Upgraded to ARP 12-point high-strength head bolts for aluminum heads.',
    torqueSpec: '100 lb-ft in 3 steps with ARP Ultra-Torque lubricant',
    reusable: false,
    replacementsOrdered: true,
    fasteners: [
      {
        id: 'fast_005',
        type: 'Socket Head Cap Screw',
        size: '1/2"-13 x 3-3/4" & 4-1/4"',
        grade: 'OEM Induction Hardened',
        finish: 'Black Oxide',
        quantityOriginal: 34,
        quantityUsable: 0,
        replacementRequired: true,
        headmarkNotes: 'ARP 145-3606 12-point kit',
      }
    ],
    createdAt: '2024-07-12T11:00:00.000Z',
    updatedAt: '2024-07-25T16:00:00.000Z',
  },
  {
    id: 'bag_004',
    subAssemblyId: 'sub_004',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-004: Quarter Panel Extension & Tail Light Housing Studs',
    binLocation: 'BIN-D04',
    status: 'plated_restored',
    notes: 'Restored speed nuts and stainless specialty studs.',
    torqueSpec: '45 in-lb (hand snug with socket)',
    reusable: true,
    replacementsOrdered: false,
    fasteners: [
      {
        id: 'fast_006',
        type: 'Specialty Stud',
        size: '1/4"-20 x 1-1/2"',
        grade: 'Grade 5',
        finish: 'Clear Zinc',
        quantityOriginal: 12,
        quantityUsable: 12,
        replacementRequired: false,
      },
      {
        id: 'fast_007',
        type: 'J-Nut / Speed Clip',
        size: '1/4"-20 Speed Nut',
        grade: 'Grade 5',
        finish: 'Black Oxide',
        quantityOriginal: 12,
        quantityUsable: 12,
        replacementRequired: false,
      }
    ],
    createdAt: '2024-08-05T15:00:00.000Z',
    updatedAt: '2024-08-15T10:00:00.000Z',
  },
  {
    id: 'bag_005',
    subAssemblyId: 'sub_005',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-005: Firewall Bulkhead Connector & Relay Mounting',
    binLocation: 'BIN-E05',
    status: 'tagged',
    notes: 'Hex washer-head self-tapping trim screws.',
    torqueSpec: '30 in-lb',
    reusable: true,
    replacementsOrdered: false,
    fasteners: [
      {
        id: 'fast_008',
        type: 'Hex Head Bolt',
        size: '#10-24 x 5/8" Slotted Indented Hex',
        grade: 'Grade 2',
        finish: 'Clear Zinc',
        quantityOriginal: 6,
        quantityUsable: 6,
        replacementRequired: false,
      }
    ],
    createdAt: '2024-09-02T11:00:00.000Z',
    updatedAt: '2024-09-02T11:00:00.000Z',
  },
  {
    id: 'bag_006',
    subAssemblyId: 'sub_006',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-006: Instrument Bezel & Dash Pad Fasteners',
    binLocation: 'BIN-F06',
    status: 'cleaning_prep',
    notes: 'Oval-head Phillips black chrome trim screws.',
    torqueSpec: 'Hand tight',
    reusable: true,
    replacementsOrdered: false,
    fasteners: [
      {
        id: 'fast_009',
        type: 'Phillips Truss Head Screw',
        size: '#8 x 3/4" Oval Head with Finishing Washer',
        grade: 'Grade 2',
        finish: 'Black Oxide',
        quantityOriginal: 16,
        quantityUsable: 14,
        replacementRequired: true,
      }
    ],
    createdAt: '2024-09-12T16:00:00.000Z',
    updatedAt: '2024-09-20T10:00:00.000Z',
  },
  {
    id: 'bag_007',
    subAssemblyId: 'sub_007',
    projectId: 'proj_charger_69_rt',
    bagLabel: 'BAG-007: Front & Rear Bumper Main Bracket Carriage Bolts',
    binLocation: 'BIN-G07',
    status: 'replaced',
    notes: 'High-polish stainless capped carriage bolts purchased for show chrome bumpers.',
    torqueSpec: '45 lb-ft',
    reusable: false,
    replacementsOrdered: true,
    fasteners: [
      {
        id: 'fast_010',
        type: 'Carriage Bolt',
        size: '7/16"-14 x 1-1/4" Smooth Polished Dome',
        grade: 'Stainless 304',
        finish: 'Chrome',
        quantityOriginal: 12,
        quantityUsable: 0,
        replacementRequired: true,
      },
      {
        id: 'fast_011',
        type: 'Hex Nut',
        size: '7/16"-14 Heavy Hex',
        grade: 'Grade 8',
        finish: 'Yellow Cadmium',
        quantityOriginal: 12,
        quantityUsable: 0,
        replacementRequired: true,
      }
    ],
    createdAt: '2024-09-16T10:30:00.000Z',
    updatedAt: '2024-09-28T14:00:00.000Z',
  }
];

export const DEFAULT_BOM_ITEMS: BOMItem[] = [
  {
    id: 'bom_001',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    partNumber: 'WIL-140-10996',
    partName: 'Wilwood Superlite 6-Piston Front Disc Brake Conversion Kit 12.88"',
    system: 'brakes',
    condition: 'Reproduction',
    status: 'Installed',
    vendor: 'Summit Racing',
    vendorPartNumber: 'WIL-140-10996-DR',
    orderTrackingNumber: '1Z9999999999999991',
    purchaseDate: '2024-05-18',
    receiptNumber: 'INV-SR-892104',
    receiptUrl: '/receipts/wilwood_front_brakes.pdf',
    quantity: 1,
    unit: 'kit',
    estimatedCost: 1950,
    actualCost: 1890,
    shippingCost: 35,
    taxCost: 145,
    coreCharge: 0,
    coreRefunded: 0,
    hollanderNumber: '560-00431',
    interchangeNotes: 'B-Body disc brake spindle bracket conversion fitment.',
    donorVehicles: ['1966-1972 Dodge Charger', '1968-1970 Plymouth Road Runner'],
    notes: 'Includes braided stainless flex hoses and BP-10 smart pads.',
    createdAt: '2024-05-10T11:00:00.000Z',
    updatedAt: '2024-06-02T15:00:00.000Z',
  },
  {
    id: 'bom_002',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    subAssemblyId: 'sub_003',
    partNumber: 'EDL-60929',
    partName: 'Edelbrock Performer RPM Aluminum Cylinder Heads (Pair 84cc)',
    system: 'engine',
    condition: 'Reproduction',
    status: 'Installed',
    vendor: 'Mancini Racing',
    vendorPartNumber: 'MRE-60929',
    orderTrackingNumber: '1Z9999999999999992',
    purchaseDate: '2024-06-20',
    receiptNumber: 'INV-MR-44021',
    receiptUrl: '/receipts/edelbrock_heads.pdf',
    quantity: 1,
    unit: 'pair',
    estimatedCost: 2400,
    actualCost: 2350,
    shippingCost: 55,
    taxCost: 180,
    coreCharge: 0,
    coreRefunded: 0,
    notes: 'Assembled with stainless 2.14/1.81 valves, dual springs for hydraulic roller camshaft.',
    createdAt: '2024-06-12T14:00:00.000Z',
    updatedAt: '2024-07-05T12:00:00.000Z',
  },
  {
    id: 'bom_003',
    projectId: 'proj_charger_69_rt',
    phase: 'disassembly_and_tagging',
    subAssemblyId: 'sub_001',
    partNumber: 'NOS-2853761',
    partName: 'OEM NOS 1969 Charger Hideaway Headlight Vacuum Actuator Cannister',
    system: 'glass_trim',
    condition: 'OEM NOS',
    status: 'Installed',
    vendor: 'Mopar Nationals Swap Meet (Vendor: Dave Chrysler Parts)',
    receiptNumber: 'SWAP-2024-082',
    receiptUrl: '/receipts/nos_actuator_bill.pdf',
    quantity: 2,
    unit: 'ea',
    estimatedCost: 250,
    actualCost: 280,
    shippingCost: 0,
    taxCost: 0,
    coreCharge: 0,
    coreRefunded: 0,
    notes: 'Mint condition New Old Stock in original blue Pentastar packaging. Diaphragm tested 100% airtight.',
    createdAt: '2024-05-15T09:00:00.000Z',
    updatedAt: '2024-06-18T10:00:00.000Z',
  },
  {
    id: 'bom_004',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    subAssemblyId: 'sub_004',
    partNumber: 'AMD-700-1568-R',
    partName: 'AMD Full Right Rear Quarter Panel Skin (18-Gauge Stamped Steel)',
    system: 'body',
    condition: 'Reproduction',
    status: 'Sourced',
    vendor: 'Auto Metal Direct',
    vendorPartNumber: 'AMD-700-1568-R',
    orderTrackingNumber: 'FREIGHT-RL-882194',
    purchaseDate: '2024-08-10',
    receiptNumber: 'INV-AMD-99120',
    receiptUrl: '/receipts/amd_quarter_panel.pdf',
    quantity: 1,
    unit: 'ea',
    estimatedCost: 750,
    actualCost: 695,
    shippingCost: 140,
    taxCost: 55,
    coreCharge: 0,
    coreRefunded: 0,
    hollanderNumber: '120-00814',
    interchangeNotes: 'Exact 1969 Charger body contour and fuel door cutouts.',
    donorVehicles: ['1969 Dodge Charger'],
    notes: 'Delivered on pallet with zero shipping dings. Pre-primed in weld-through zinc.',
    createdAt: '2024-07-28T16:00:00.000Z',
    updatedAt: '2024-08-22T14:00:00.000Z',
  },
  {
    id: 'bom_005',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    partNumber: 'SAG-560-00215',
    partName: 'Saginaw 800 Quick-Ratio 12.7:1 Firm Feel Power Steering Gearbox',
    system: 'steering',
    condition: 'Remanufactured',
    status: 'Sourced',
    vendor: 'Firm Feel Inc.',
    vendorPartNumber: 'FF-MOP-STG3',
    purchaseDate: '2024-08-28',
    receiptNumber: 'INV-FF-3401',
    receiptUrl: '/receipts/firm_feel_box.pdf',
    quantity: 1,
    unit: 'ea',
    estimatedCost: 420,
    actualCost: 390,
    shippingCost: 30,
    taxCost: 28,
    coreCharge: 75,
    coreRefunded: 75,
    hollanderNumber: '560-00215',
    interchangeNotes: 'Modern quick ratio valving in original cast iron casing.',
    donorVehicles: ['1966-1972 Mopar B-Body', '1970-1974 E-Body'],
    notes: 'Core returned and full $75 deposit credited back to credit card.',
    createdAt: '2024-08-15T11:30:00.000Z',
    updatedAt: '2024-09-10T16:00:00.000Z',
  },
  {
    id: 'bom_006',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    partNumber: 'DANA-60-B-CORE',
    partName: 'Original Mopar B-Body Dana 60 9-3/4" Rear Axle Housing (Sure-Grip 3.54:1)',
    system: 'drivetrain',
    condition: 'Salvage',
    status: 'Sourced',
    vendor: 'Classic Mopar Salvage Yard (Denver, CO)',
    purchaseDate: '2024-07-02',
    receiptNumber: 'SALV-2024-419',
    receiptUrl: '/receipts/dana60_salvage_receipt.pdf',
    quantity: 1,
    unit: 'assembly',
    estimatedCost: 1500,
    actualCost: 1350,
    shippingCost: 180,
    taxCost: 0,
    coreCharge: 0,
    coreRefunded: 0,
    hollanderNumber: '190-00512',
    interchangeNotes: 'Factory correct 59.25" wheel-to-wheel track width for 1968-1970 B-Bodies.',
    donorVehicles: ['1968-1970 Dodge Charger 4-Speed', '1968-1970 Plymouth GTX'],
    notes: 'Original casting date 1969. Gears in immaculate condition, backlash 0.008".',
    createdAt: '2024-06-25T13:00:00.000Z',
    updatedAt: '2024-07-15T11:00:00.000Z',
  },
  {
    id: 'bom_007',
    projectId: 'proj_charger_69_rt',
    phase: 'electrical',
    subAssemblyId: 'sub_005',
    partNumber: 'AAW-510103',
    partName: 'American Autowire Classic Update Complete Body & Engine Wiring Harness',
    system: 'electrical',
    condition: 'Reproduction',
    status: 'Needed',
    vendor: 'Classic Industries',
    vendorPartNumber: 'MB2491',
    quantity: 1,
    unit: 'kit',
    estimatedCost: 925,
    actualCost: 0,
    shippingCost: 0,
    taxCost: 0,
    coreCharge: 0,
    coreRefunded: 0,
    notes: 'Needed before interior and engine bay reassembly. Supports HEI ignition and modern alternator.',
    createdAt: '2024-09-01T10:00:00.000Z',
    updatedAt: '2024-09-01T10:00:00.000Z',
  },
  {
    id: 'bom_008',
    projectId: 'proj_charger_69_rt',
    phase: 'interior',
    subAssemblyId: 'sub_006',
    partNumber: 'LEG-69BC-BLK',
    partName: 'Legendary Auto Interiors Front Bucket Seat Foam & Vinyl Upholstery Kit',
    system: 'interior',
    condition: 'Reproduction',
    status: 'Needed',
    vendor: 'Legendary Auto Interiors',
    vendorPartNumber: 'AA69C-0101',
    quantity: 1,
    unit: 'set',
    estimatedCost: 1250,
    actualCost: 0,
    shippingCost: 0,
    taxCost: 0,
    coreCharge: 0,
    coreRefunded: 0,
    notes: 'Original Madrid grain black vinyl with authentic dielectric heat-sealed pleats.',
    createdAt: '2024-09-05T12:00:00.000Z',
    updatedAt: '2024-09-05T12:00:00.000Z',
  },
  {
    id: 'bom_009',
    projectId: 'proj_charger_69_rt',
    phase: 'final_assembly',
    subAssemblyId: 'sub_007',
    partNumber: 'MOP-2949437',
    partName: 'OEM NOS 1969 Charger R/T Grille Center Emblem',
    system: 'glass_trim',
    condition: 'OEM NOS',
    status: 'Needed',
    vendor: 'eBay Collector (Seller: mopar_rare_parts)',
    quantity: 1,
    unit: 'ea',
    estimatedCost: 380,
    actualCost: 0,
    shippingCost: 0,
    taxCost: 0,
    coreCharge: 0,
    coreRefunded: 0,
    hollanderNumber: '820-00109',
    notes: 'Targeting mint NOS emblem to complete front grille assembly without pitting.',
    createdAt: '2024-09-15T15:00:00.000Z',
    updatedAt: '2024-09-15T15:00:00.000Z',
  },
  {
    id: 'bom_010',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    partNumber: 'QA1-52851',
    partName: 'QA1 1-1/4" Front Hollow Chromoly Performance Sway Bar Kit',
    system: 'suspension',
    condition: 'Reproduction',
    status: 'Installed',
    vendor: 'Summit Racing',
    vendorPartNumber: 'QA1-52851',
    purchaseDate: '2024-06-05',
    receiptNumber: 'INV-SR-899120',
    receiptUrl: '/receipts/qa1_swaybar.pdf',
    quantity: 1,
    unit: 'kit',
    estimatedCost: 320,
    actualCost: 310,
    shippingCost: 0,
    taxCost: 24,
    coreCharge: 0,
    coreRefunded: 0,
    notes: 'Includes polyurethane greaseable frame bushings and heavy-duty end links.',
    createdAt: '2024-05-25T14:00:00.000Z',
    updatedAt: '2024-06-15T11:00:00.000Z',
  }
];

export const DEFAULT_SUBLET_EXPENSES: SubletExpense[] = [
  {
    id: 'sublet_001',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    vendorName: 'Mopar Performance Machine (Omaha, NE)',
    serviceCategory: 'Machine Shop',
    description: '440 block ultrasonic test, hot tank, deck square, bore .030 over, torque plate hone, balance rotating assembly',
    invoiceNumber: 'INV-MPM-8842',
    invoiceDate: '2024-07-28',
    cost: 1950,
    receiptUrl: '/receipts/machine_shop_block.pdf',
    createdAt: '2024-07-30T10:00:00.000Z',
  },
  {
    id: 'sublet_002',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    vendorName: 'Apex Industrial Powder Coating',
    serviceCategory: 'Plating & Coating',
    description: 'Sandblast to white metal and electrostatic powder coat front K-member, control arms, and rear axle housing in 30% semi-gloss black',
    invoiceNumber: 'INV-APC-2209',
    invoiceDate: '2024-06-25',
    cost: 850,
    receiptUrl: '/receipts/powder_coat_invoice.pdf',
    createdAt: '2024-06-26T12:00:00.000Z',
  },
  {
    id: 'sublet_003',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    vendorName: 'Precision Media Blasting Specialists',
    serviceCategory: 'Body & Paint',
    description: 'Complete unibody rotisserie media blast with plastic bead media, epoxy etching primer wash coat applied within 2 hours',
    invoiceNumber: 'INV-PMB-1194',
    invoiceDate: '2024-05-30',
    cost: 2150,
    receiptUrl: '/receipts/media_blast_body.pdf',
    createdAt: '2024-06-01T09:00:00.000Z',
  }
];

export const DEFAULT_LABOR_RECORDS: LaborRecord[] = [
  {
    id: 'lab_001',
    projectId: 'proj_charger_69_rt',
    phase: 'disassembly_and_tagging',
    taskDescription: 'Complete unbolting of exterior trim, glass removal, interior gutting, drivetrain extraction',
    hours: 48,
    hourlyRate: 85,
    technicianName: 'Marcus Vance (Lead Restorer)',
    date: '2024-05-15',
  },
  {
    id: 'lab_002',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    taskDescription: 'Rebuild front K-member, press polyurethane bushings, fit Wilwood disc hubs and calipers',
    hours: 32,
    hourlyRate: 85,
    technicianName: 'Marcus Vance (Lead Restorer)',
    date: '2024-06-30',
  },
  {
    id: 'lab_003',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    taskDescription: 'Assemble 440 short block, degree Comp Cams camshaft, set piston ring end gaps to .022"',
    hours: 28,
    hourlyRate: 85,
    technicianName: 'Dale K. (Engine Specialist)',
    date: '2024-08-10',
  }
];

export const DEFAULT_PHOTO_LOGS: PhotoLog[] = [
  {
    id: 'photo_001',
    projectId: 'proj_charger_69_rt',
    phase: 'disassembly_and_tagging',
    subAssemblyId: 'sub_001',
    category: 'before_after_pair',
    title: 'Baseline As-Found Barn Find Discovery & VIN Fender Tag',
    description: 'Vehicle untouched in dry Colorado storage since 1988. Authentic numbers-matching fender tag intact.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1552519507-da3b142c6e3d?auto=format&fit=crop&w=800&q=80',
    afterImageUrl: 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-04-18T14:00:00.000Z',
    tags: ['vin_plate', 'fender_tag', 'as_found', 'body'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-04-18T14:00:00.000Z',
  },
  {
    id: 'photo_002',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    category: 'before_after_pair',
    title: 'Front K-Member: Greasy Factory As-Found vs. QA1 Powder-Coated Rebuild',
    description: 'Factory stamped K-member full of road grime compared with completed QA1 tubular suspension and Wilwood 6-piston disc brakes.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1486006920555-c77dce18193b?auto=format&fit=crop&w=800&q=80',
    afterImageUrl: 'https://images.unsplash.com/photo-1617814076367-b759c7d7e738?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-06-28T16:30:00.000Z',
    tags: ['suspension', 'brakes', 'k_member', 'before_after'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-06-28T16:30:00.000Z',
  },
  {
    id: 'photo_003',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    subAssemblyId: 'sub_003',
    category: 'restoration_in_progress',
    title: '440 RB Big Block Machined & Rotating Assembly Balancing',
    description: 'Forged crankshaft installed with plastigauge checking main bearing clearance at .0025".',
    beforeImageUrl: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-08-08T11:00:00.000Z',
    tags: ['engine', 'crankshaft', 'machining'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-08-08T11:00:00.000Z',
  },
  {
    id: 'photo_004',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    subAssemblyId: 'sub_004',
    category: 'restoration_in_progress',
    title: 'Quarter Panel Rust Repair & AMD Metal Fabrication Alignment',
    description: 'Quarter panel fitment clamped and test-hung with rear door gap set to precise 3/16" factory spacing.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1541348263662-e0c8de4259ba?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-08-25T15:00:00.000Z',
    tags: ['metalwork', 'quarter_panel', 'welding'],
    includeInDossier: true,
    milestoneProof: false,
    createdAt: '2024-08-25T15:00:00.000Z',
  },
  {
    id: 'photo_005',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    category: 'restoration_in_progress',
    title: 'Rotisserie Epoxy Primer Guide Coat Blocking',
    description: 'Unibody blasted and sealed in black epoxy primer, undergoing first stage 240-grit dry block sanding.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1502877338535-766e1452684a?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-09-02T10:00:00.000Z',
    tags: ['epoxy_primer', 'block_sanding', 'rotisserie'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-09-02T10:00:00.000Z',
  },
  {
    id: 'photo_006',
    projectId: 'proj_charger_69_rt',
    phase: 'electrical',
    subAssemblyId: 'sub_005',
    category: 'disassembly_routing',
    title: 'Original Firewall Bulkhead Routing Documentation',
    description: 'Baseline routing capture of factory ammeter wiring harness before removal to prevent reassembly errors.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1558981403-c5f9899a28bc?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-05-08T13:45:00.000Z',
    tags: ['wiring', 'firewall', 'routing'],
    includeInDossier: false,
    milestoneProof: false,
    createdAt: '2024-05-08T13:45:00.000Z',
  },
  {
    id: 'photo_007',
    projectId: 'proj_charger_69_rt',
    phase: 'interior',
    subAssemblyId: 'sub_006',
    category: 'before_after_pair',
    title: 'Rallye Gauge Cluster: Faded Gauges vs. Restored Quartz Tic-Toc-Tach',
    description: 'Original cracked lens and faded needles restored to factory silk-screened markings with tested quartz movement.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1511919884226-fd3cad34687c?auto=format&fit=crop&w=800&q=80',
    afterImageUrl: 'https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-09-22T14:15:00.000Z',
    tags: ['interior', 'gauges', 'cluster', 'tic_toc_tach'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-09-22T14:15:00.000Z',
  },
  {
    id: 'photo_008',
    projectId: 'proj_charger_69_rt',
    phase: 'final_assembly',
    subAssemblyId: 'sub_007',
    category: 'completed_restored',
    title: 'Show Chrome Front Bumper & Restored Hideaway Grille Fit Check',
    description: 'Front bumper freshly triple-plated, mock assembled with re-chromed center uprights and hideaways.',
    beforeImageUrl: 'https://images.unsplash.com/photo-1553440569-bcc63803a83d?auto=format&fit=crop&w=800&q=80',
    capturedAt: '2024-09-29T16:00:00.000Z',
    tags: ['bumpers', 'chrome', 'grille', 'final_assembly'],
    includeInDossier: true,
    milestoneProof: true,
    createdAt: '2024-09-29T16:00:00.000Z',
  }
];

export const DEFAULT_TASKS: RestorationTask[] = [
  {
    id: 'task_001',
    projectId: 'proj_charger_69_rt',
    phase: 'disassembly_and_tagging',
    subAssemblyId: 'sub_001',
    title: 'Remove and tag hideaway grille & vacuum actuators',
    description: 'Label vacuum hoses (red/yellow stripes) and bag fasteners in BAG-001.',
    status: 'completed',
    priority: 'high',
    estimatedHours: 4,
    actualHours: 3.5,
    assignedTo: 'Marcus Vance',
    completedAt: '2024-05-02T14:00:00.000Z',
    createdAt: '2024-05-01T08:00:00.000Z',
    updatedAt: '2024-05-02T14:00:00.000Z',
  },
  {
    id: 'task_002',
    projectId: 'proj_charger_69_rt',
    phase: 'chassis_and_suspension',
    subAssemblyId: 'sub_002',
    title: 'Assemble QA1 tubular K-member and torqued control arms',
    description: 'Install Moog eccentric cam bolts, QA1 sway bar, and Wilwood disc hubs.',
    status: 'completed',
    priority: 'critical',
    estimatedHours: 8,
    actualHours: 9,
    assignedTo: 'Marcus Vance',
    completedAt: '2024-06-29T17:00:00.000Z',
    createdAt: '2024-06-25T08:00:00.000Z',
    updatedAt: '2024-06-29T17:00:00.000Z',
  },
  {
    id: 'task_003',
    projectId: 'proj_charger_69_rt',
    phase: 'powertrain',
    subAssemblyId: 'sub_003',
    title: 'Torque cylinder heads with ARP 12-point bolts',
    description: 'Follow 3-step torque sequence up to 100 lb-ft with ARP assembly lube.',
    status: 'completed',
    priority: 'critical',
    estimatedHours: 4,
    actualHours: 4,
    assignedTo: 'Dale K.',
    completedAt: '2024-08-12T11:00:00.000Z',
    createdAt: '2024-08-10T09:00:00.000Z',
    updatedAt: '2024-08-12T11:00:00.000Z',
  },
  {
    id: 'task_004',
    projectId: 'proj_charger_69_rt',
    phase: 'sheet_metal_and_paint',
    subAssemblyId: 'sub_004',
    title: 'Spot weld AMD passenger quarter panel skin',
    description: 'Ensure trunk seam and wheel arch flange match OEM spot weld spacing.',
    status: 'in_progress',
    priority: 'high',
    estimatedHours: 16,
    actualHours: 10,
    assignedTo: 'Body Tech Jim',
    createdAt: '2024-09-18T08:00:00.000Z',
    updatedAt: '2024-09-26T15:00:00.000Z',
  },
  {
    id: 'task_005',
    projectId: 'proj_charger_69_rt',
    phase: 'electrical',
    subAssemblyId: 'sub_005',
    title: 'Route American Autowire firewall main harness',
    description: 'Awaiting harness arrival from Classic Industries.',
    status: 'blocked',
    priority: 'medium',
    estimatedHours: 12,
    actualHours: 0,
    blockingReason: 'Part on backorder (AAW-510103)',
    createdAt: '2024-09-20T10:00:00.000Z',
    updatedAt: '2024-09-20T10:00:00.000Z',
  },
  {
    id: 'task_006',
    projectId: 'proj_charger_69_rt',
    phase: 'interior',
    subAssemblyId: 'sub_006',
    title: 'Install rebuilt Tic-Toc-Tach and Rallye instrument cluster',
    description: 'Connect speedo cable and wire bench-tested gauge illumination bulbs.',
    status: 'todo',
    priority: 'medium',
    estimatedHours: 6,
    actualHours: 0,
    createdAt: '2024-09-25T11:00:00.000Z',
    updatedAt: '2024-09-25T11:00:00.000Z',
  },
  {
    id: 'task_007',
    projectId: 'proj_charger_69_rt',
    phase: 'final_assembly',
    subAssemblyId: 'sub_007',
    title: 'Final shakedown road test and wheel alignment',
    description: 'Set front camber to -0.5 deg, caster +4.5 deg, toe 1/16" in.',
    status: 'todo',
    priority: 'critical',
    estimatedHours: 5,
    actualHours: 0,
    createdAt: '2024-09-28T09:00:00.000Z',
    updatedAt: '2024-09-28T09:00:00.000Z',
  }
];
